import { t } from 'i18next'
import { Api, ApiError } from '../api'

const STORAGE_KEY = 'support_chat'
const MAX_MESSAGE_LENGTH = 4000

/**
 * Client for the support assistant (`POST support-chat/messages`). The
 * api-backend routes a member's token to the member assistant, keeps the
 * conversation per `sessionId` and owns the session id, so the first message
 * of a conversation is sent without one.
 */
class SupportChatService {
  static MAX_MESSAGE_LENGTH = MAX_MESSAGE_LENGTH

  static sendMessage({ message, sessionId }: SendMessageParams) {
    return Api.post(
      'support-chat/messages',
      { message, ...(sessionId && { sessionId }) },
      { memberAuth: true }
    )
  }

  static getErrorMessage(error: unknown) {
    const fallback = t(
      'The help assistant is unavailable right now. Please try again in a moment.'
    )

    if (!(error instanceof ApiError)) {
      return fallback
    }

    const { status } = error as ApiError & { status?: number }
    if (status === 400 && error.message) {
      return error.message
    }
    return fallback
  }

  /** Whether the session can no longer be used and a new one must start. */
  static isSessionRejected(error: unknown) {
    return (
      error instanceof ApiError &&
      (error as ApiError & { status?: number }).status === 403
    )
  }

  /**
   * The conversation is kept per browser tab and per member, so a refresh keeps
   * it and another member signing in on the same tab never sees it.
   */
  static restore(userId: number): StoredState | undefined {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY)
      if (!raw) {
        return undefined
      }
      const parsed = JSON.parse(raw)
      if (!parsed || parsed.userId !== userId) {
        return undefined
      }
      return {
        userId,
        sessionId:
          typeof parsed.sessionId === 'string' ? parsed.sessionId : undefined,
        messages: Array.isArray(parsed.messages) ? parsed.messages : [],
      }
    } catch {
      return undefined
    }
  }

  static persist(state: StoredState) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch {
      // Storage may be unavailable (private mode, quota); the chat still works in memory
    }
  }

  static clear() {
    try {
      sessionStorage.removeItem(STORAGE_KEY)
    } catch {
      // ignore
    }
  }
}

type SendMessageParams = {
  message: string
  sessionId?: string
}

type SupportChatMessage = {
  id: string
  role: 'user' | 'assistant'
  /** Markdown for assistant messages, plain text for the member's own */
  content: string
  citations: Dto.SupportChatCitation[]
}

type StoredState = {
  userId: number
  sessionId?: string
  messages: SupportChatMessage[]
}

export { SupportChatService }
export type { SupportChatMessage }
